import React from 'react';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { Chip } from '@mui/material';
import { Typography } from '@mui/material';
import { Accordion } from '@mui/material';
import { AccordionSummary } from '@mui/material';
import { AccordionDetails } from '@mui/material';
import { Alert } from '@mui/material';
import { Stack } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';

export interface ModelExperimentationFilterDefinitionForSelections {
  columnHeaderValue: string;
  uniqueValues: string[];
}

export interface ModelExperimentationMultiColumnFilterSelectionsProps {
  filterDefinitions: ModelExperimentationFilterDefinitionForSelections[];
  selections: Record<string, string[]>;
  onSelectionsChange: (next: Record<string, string[]>) => void;
  disabled?: boolean;
  accordionTitle?: string;
  emptyMessage?: string;
  renderBelowColumn?: (columnHeaderValue: string) => React.ReactNode;
}

export const ModelExperimentationMultiColumnFilterSelections: React.FC<
  ModelExperimentationMultiColumnFilterSelectionsProps
> = ({
  filterDefinitions,
  selections,
  onSelectionsChange,
  disabled = false,
  accordionTitle = 'Filters',
  emptyMessage = 'No filter columns in schema',
  renderBelowColumn,
}) => {
  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {accordionTitle}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 3, pb: 2 }}>
        <Stack spacing={2}>
          {filterDefinitions.length === 0 ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              {emptyMessage}
            </Alert>
          ) : (
            filterDefinitions.map(({ columnHeaderValue, uniqueValues }) => {
              const list = uniqueValues.map((value) => ({
                optionLabel: value,
                optionValue: value,
              }));
              const selected = selections[columnHeaderValue] ?? [];
              const removeValue = (value: string) => {
                onSelectionsChange({
                  ...selections,
                  [columnHeaderValue]: (selected ?? []).filter(
                    (v) => v !== value,
                  ),
                });
              };
              return (
                <Box key={columnHeaderValue}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1.5,
                      fontWeight: 700,
                      color: 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {columnHeaderValue}
                    <Chip
                      label={
                        selected.length === 0
                          ? `${uniqueValues.length} values`
                          : `${selected.length} selected`
                      }
                      size="small"
                      sx={{ ml: 1, height: 20 }}
                      color="primary"
                      variant="outlined"
                    />
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap="xs" mb="sm">
                    {selected.length > 0 ? (
                      selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          onDelete={
                            disabled ? undefined : () => removeValue(value)
                          }
                          sx={{
                            backgroundColor: 'rgba(99, 102, 241, 0.08)',
                            color: 'rgb(67, 56, 202)',
                            fontWeight: 500,
                            '&:hover': {
                              backgroundColor: 'rgba(99, 102, 241, 0.15)',
                            },
                          }}
                        />
                      ))
                    ) : (
                      <Chip
                        label="None selected"
                        size="small"
                        sx={{
                          backgroundColor: 'action.hover',
                          color: 'text.secondary',
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </Box>
                  <SelectBoxWithSearch
                    type="multi"
                    list={list}
                    label={null}
                    selected={selected}
                    setSelected={(values) => {
                      if (typeof values === 'function') return;
                      onSelectionsChange({
                        ...selections,
                        [columnHeaderValue]: values ?? [],
                      });
                    }}
                    disabled={disabled}
                  />
                  {renderBelowColumn?.(columnHeaderValue)}
                  {uniqueValues.length === 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      No values available for this column
                    </Typography>
                  )}
                </Box>
              );
            })
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
